// The rebinding screen's model (gate A4.2) on the input's own rebinding model (@rpg/runtime's `Rebinding`, gate
// A2.1): @rpg/ui's small `RebindModel` interface over the real action maps, so a change on the controls screen is a
// change to the bindings the game reads, checked against the input contract.
//
// - Devices are the screen's tabs: keyboard (the runtime's keyboard-and-mouse family), gamepad, touch.
// - Slots: each of an action's bindings on a device gives slots: a dpad four (up, down, left, right), a button one
//   per control ("main", then "alt1", …); a later binding of the same action on the device prefixes its slots with
//   its number ("2.up"). Bindings the runtime doesn't rebind (touch, sticks, pointer motion) are shown fixed.
// - Captures listen on the window before anything else (a key) or poll the pads (a button), so a press made to
//   rebind never reaches the game; Escape cancels. The keyboard tab captures keys (a mouse button's slot takes a key).
// - A change that leaves two actions of one context on a control is kept and shown as a conflict ("allow"), as the
//   screen's design asks; the contract's other rules still refuse a bad change.
// - What the game saves (with the settings, synced) is the runtime's own prefs (`rpg.input-prefs@1`).
import type { DeviceFamily, RebindConflict, RebindModel, RebindSlot } from "@rpg/ui";
import type { BindingInfo, DeviceFamily as InputFamily, InputPrefs, Rebinding } from "@rpg/runtime";

/** The model, plus what the game persists and tests read. */
export interface GameRebind extends RebindModel {
  /** The player's changes as the runtime stores them, what the game saves. */
  overrides(): InputPrefs;
  /** Apply saved prefs (bad entries dropped, reported). */
  load(prefs: unknown): void;
  /** Called after every change. */
  onChange(fn: () => void): void;
  /** The capture in progress, for tests: its device, or null. */
  capturing(): DeviceFamily | null;
}

const FAMILY: Record<string, InputFamily> = { keyboard: "keyboardMouse", gamepad: "gamepad", touch: "touch" };
const DIRECTIONS = ["up", "down", "left", "right"] as const; // a dpad's control order

/** One slot and where it lives in the runtime's bindings. */
interface Placed extends RebindSlot {
  binding: string;
  part: number;
  dpad: boolean;
}

export function createRebindModel(o: {
  rebinding: Rebinding;
  /** The actions in list order with their label string ids. */
  actions: readonly { id: string; label: string }[];
  pads?: () => readonly (Gamepad | null)[];
  captureTimeoutMs?: number;
}): GameRebind {
  const r = o.rebinding;
  const devices: DeviceFamily[] = ["keyboard", "gamepad", "touch"];
  let capturing: DeviceFamily | null = null;
  const listeners: (() => void)[] = [];
  r.onChange(() => listeners.forEach((l) => l()));

  const placed = (action: string, device: DeviceFamily): Placed[] => {
    const info = r.list().find((a) => a.id === action);
    if (!info) return [];
    const out: Placed[] = [];
    info.bindings
      .filter((b: BindingInfo) => b.family === FAMILY[device])
      .forEach((b, k) => {
        const prefix = k === 0 ? "" : `${k + 1}.`;
        if (b.shape === "dpad")
          DIRECTIONS.forEach((d, i) => out.push({ id: `${prefix}${d}`, label: `rebind.slot.${d}`, control: b.controls[i] || null, fixed: !b.rebindable, binding: b.id, part: i, dpad: true }));
        else if (b.rebindable)
          b.controls.forEach((c, i) => out.push({ id: `${prefix}${i === 0 ? "main" : `alt${i}`}`, control: c || null, binding: b.id, part: i, dpad: false }));
        else out.push({ id: `${prefix}main`, control: b.controls[0] ?? null, fixed: true, binding: b.id, part: 0, dpad: false });
      });
    return out;
  };
  const strip = (p: Placed): RebindSlot => ({ id: p.id, ...(p.label ? { label: p.label } : {}), control: p.control, ...(p.fixed ? { fixed: true } : {}) });

  const conflicts = (device?: DeviceFamily): RebindConflict[] => {
    const out: RebindConflict[] = [];
    for (const k of r.conflicts()) {
      const d = devices.find((x) => k.bindings.some((id) => r.binding(id)?.family === FAMILY[x]));
      if (!d || (device && d !== device)) continue;
      const bindings: { action: string; slot: string }[] = [];
      for (const id of k.bindings) {
        const b = r.binding(id);
        if (!b) continue;
        const slot = placed(b.action, d).find((s) => s.binding === id && s.control === k.control);
        if (slot && !bindings.some((x) => x.action === b.action && x.slot === slot.id)) bindings.push({ action: b.action, slot: slot.id });
      }
      out.push({ device: d, control: k.control, bindings });
    }
    return out;
  };

  return {
    devices: () => devices,
    actions: () => o.actions,
    bindings: (action, device) => placed(action, device).map(strip),
    capture(device, done) {
      capturing = device;
      let raf = 0;
      let finished = false;
      const before = new Set<number>();
      const pads = o.pads ?? (() => (navigator.getGamepads ? navigator.getGamepads() : []));
      for (const p of pads()) p?.buttons.forEach((b, i) => b.pressed && before.add(i));
      const finish = (control: string | null): void => {
        if (finished) return;
        finished = true;
        capturing = null;
        removeEventListener("keydown", onKey, true);
        if (raf) cancelAnimationFrame(raf);
        clearTimeout(timer);
        done(control);
      };
      const onKey = (e: KeyboardEvent): void => {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (e.code === "Escape") return finish(null);
        if (device === "keyboard") finish(e.code);
      };
      const poll = (): void => {
        raf = 0;
        for (const p of pads()) {
          const i = p?.buttons.findIndex((b, k) => b.pressed && !before.has(k)) ?? -1;
          if (i >= 0) return finish(`Pad${i}`);
          p?.buttons.forEach((b, k) => !b.pressed && before.delete(k));
        }
        raf = requestAnimationFrame(poll);
      };
      addEventListener("keydown", onKey, true);
      if (device === "gamepad") raf = requestAnimationFrame(poll);
      const timer = setTimeout(() => finish(null), o.captureTimeoutMs ?? 10_000);
      return () => finish(null);
    },
    set(action, device, slot, control) {
      const p = placed(action, device).find((s) => s.id === slot);
      if (!p) throw new Error(`rebind: no slot ${action}/${slot} on ${device}`);
      if (p.fixed) throw new Error(`rebind: ${action}/${slot} on ${device} is fixed`);
      const res = r.setPart(p.binding, p.part, control, { conflicts: "allow" });
      if (!res.ok) console.warn(`[rebind] ${action}/${slot} → ${control} refused: ${res.reason}`);
      return conflicts(device);
    },
    reset(action, device) {
      if (!action && !device) return r.reset();
      for (const a of r.list()) {
        if (action && a.id !== action) continue;
        for (const b of a.bindings) if (!device || b.family === FAMILY[device]) if (b.overridden) r.reset(b.id);
      }
    },
    conflicts,
    overrides: () => r.prefs(),
    load(prefs) {
      const issues = r.applyPrefs(prefs);
      if (issues.length) console.warn(`[rebind] saved bindings: ${issues.join("; ")}`);
    },
    onChange: (fn) => void listeners.push(fn),
    capturing: () => capturing,
  };
}
